/**
 * Contratos do motor de sincronizacao.
 *
 * O motor nao conhece Dexie nem Supabase: fala com duas interfaces. E por isso
 * que ele pode ser testado contra um Postgres de verdade sem navegador nenhum —
 * e um motor de sync que nunca foi testado sob falha e um motor que perde o dia
 * de coleta de alguem.
 */

export type SyncOp = 'upsert' | 'delete';

export interface OutboxItem {
  /** Chave da idempotencia. UNIQUE no servidor: push repetido vira no-op. */
  clientMutationId: string;
  entity: string;
  entityId: string;
  op: SyncOp;
  payload: Record<string, unknown>;
  createdAt: number;
  attempts: number;
  lastError: string | null;
}

export type PushStatus = 'applied' | 'duplicate' | 'rejected';

export interface PushOutcome {
  clientMutationId: string;
  status: PushStatus;
  reason: string | null;
}

export interface PushResult {
  results: PushOutcome[];
  serverSeq: number;
}

export interface RemoteChange {
  server_seq: number;
  entity: string;
  entity_id: string;
  op: SyncOp;
  payload: Record<string, unknown>;
  created_at: string;
}

export interface PullResult {
  changes: RemoteChange[];
  cursor: number;
  hasMore: boolean;
}

/** O servidor, como o motor o enxerga. */
export interface SyncTransport {
  push(batch: OutboxItem[], deviceId: string): Promise<PushResult>;
  pull(since: number, limit: number): Promise<PullResult>;
}

export interface SyncState {
  deviceId: string;
  lastServerSeq: number;
  lastPushAt: number | null;
  lastPullAt: number | null;
  /** Ultima rodada completa (envio e recebimento) sem nenhum erro. */
  lastSyncAt: number | null;
  /** Erros da ultima rodada; vazio quando deu tudo certo. */
  lastErrors: string[];
}

export const estadoInicial = (deviceId: string): SyncState => ({
  deviceId,
  lastServerSeq: 0,
  lastPushAt: null,
  lastPullAt: null,
  lastSyncAt: null,
  lastErrors: [],
});

/** Um item que o servidor recusou e espera decisao de uma pessoa. */
export interface ItemEmQuarentena {
  id: number;
  item: OutboxItem;
  reason: string;
  at: number;
}

/**
 * Recusa por falta de permissao (usuario fora da equipe). Nao e problema do
 * dado, e sim do cadastro da pessoa: o item fica na fila ate o admin resolver.
 */
export const MOTIVO_SEM_PERMISSAO = 'sem permissao de escrita';

/** O armazenamento local, como o motor o enxerga. */
export interface LocalStore {
  enqueue(item: OutboxItem): Promise<void>;
  /**
   * Escrita feita no proprio aparelho: grava a linha na tabela local E o item na
   * fila, na mesma transacao. Sem isso o dado so apareceria na tela depois de ir
   * ao servidor e voltar — e no haras nao ha servidor.
   */
  gravarLocal(item: OutboxItem): Promise<void>;
  peekOutbox(limit: number): Promise<OutboxItem[]>;
  dropFromOutbox(clientMutationIds: string[]): Promise<void>;
  recordFailure(clientMutationId: string, error: string): Promise<void>;
  /**
   * Grava so no aparelho, sem ir para a fila. E o reflexo local de uma emenda:
   * a linha corrigida nao sobe (o servidor recusaria a escrita na ficha
   * fechada); sobe a `amendment`, e o servidor aplica.
   */
  gravarSoLocal(entity: string, entityId: string, patch: Record<string, unknown>): Promise<void>;
  /** Itens rejeitados pelo servidor, que precisam de decisao humana. */
  quarantine(item: OutboxItem, reason: string): Promise<void>;
  /** Quarentena na ordem em que os itens foram escritos no aparelho. */
  listQuarantine(): Promise<ItemEmQuarentena[]>;
  /**
   * Devolve o item a fila, na posicao original (createdAt) e com o mesmo
   * clientMutationId: um item recusado nunca entrou no change_log, entao o
   * reenvio nao vira duplicata.
   */
  requeueFromQuarantine(id: number, ajuste?: Record<string, unknown>): Promise<void>;
  /**
   * Tira o item da quarentena e apaga a linha local que ele alterou, se nao
   * houver outra escrita pendente para ela. O pull reconstroi a linha como o
   * servidor a tem.
   */
  discardFromQuarantine(id: number): Promise<void>;
  /**
   * Aplica o que veio do servidor. Pula a entidade que ainda tem item pendente
   * na fila: a edicao local e mais nova e nao pode ser sobrescrita antes de
   * subir. A versao do servidor chega na rodada seguinte.
   */
  applyRemote(changes: RemoteChange[]): Promise<void>;
  getSyncState(): Promise<SyncState>;
  setSyncState(state: SyncState): Promise<void>;
  pendingCount(): Promise<number>;
}

export interface SyncReport {
  pushed: number;
  duplicates: number;
  rejected: number;
  pulled: number;
  pending: number;
  cursor: number;
  errors: string[];
}
